import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import { 
  Heart, LogOut, Users, Store, Gift,
  UserPlus, Shield, Trash2, Building2, Globe,
  Wallet, CheckCircle2, XCircle, Clock, Eye, CreditCard, AlertTriangle,
  FileText, Settings, BarChart3, Search, RefreshCw, Link2
} from 'lucide-react';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

const PARTNER_CODE_FORM_DEFAULT = {
  code: '',
  label: '',
  description: '',
  fee_percentage: '3',
  max_redemptions: '1',
  expires_at: '',
  is_active: true,
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toApiDateTimeValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [users, setUsers] = useState([]);
  const [reconciliationData, setReconciliationData] = useState({
    summary: null,
    payments: [],
    withdrawals: []
  });
  const [cashInData, setCashInData] = useState({
    summary: null,
    entries: [],
    updated_at: null
  });
  const [reconciliationFilters, setReconciliationFilters] = useState({
    payment_id: '',
    external_reference: '',
    payout_id: ''
  });
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);
  const [loadingCashIn, setLoadingCashIn] = useState(false);
  const [cashInLastRefresh, setCashInLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showPartnerCodeModal, setShowPartnerCodeModal] = useState(false);
  const [editingPartnerCode, setEditingPartnerCode] = useState(null);
  const [newAdminData, setNewAdminData] = useState({ name: '', email: '', password: '' });
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [processingSupplierId, setProcessingSupplierId] = useState(null);
  const [partnerCodes, setPartnerCodes] = useState([]);
  const [loadingPartnerCodes, setLoadingPartnerCodes] = useState(false);
  const [partnerCodeForm, setPartnerCodeForm] = useState(PARTNER_CODE_FORM_DEFAULT);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const navigate = useNavigate();

  const formatAdminDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('pt-BR');
  };

  const getUserPlanLabel = (user) => {
    if (user?.subscription_plan === 'premium') return 'Premium';
    return 'Grátis';
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== 'cashin') return undefined;
    fetchCashIn({ silent: true });
    const intervalId = setInterval(() => {
      fetchCashIn({ silent: true });
    }, 15000);
    return () => clearInterval(intervalId);
  }, [activeTab]);

  const fetchReconciliation = async (
    nextFilters = reconciliationFilters,
    { silent = false } = {}
  ) => {
    setLoadingReconciliation(true);
    try {
      const query = new URLSearchParams({ limit: '120' });
      if (nextFilters.payment_id?.trim()) query.set('payment_id', nextFilters.payment_id.trim());
      if (nextFilters.external_reference?.trim()) query.set('external_reference', nextFilters.external_reference.trim());
      if (nextFilters.payout_id?.trim()) query.set('payout_id', nextFilters.payout_id.trim());

      const response = await axios.get(`${API}/admin/reconciliation?${query.toString()}`, { withCredentials: true });
      setReconciliationData(normalizeObjectStrings(response.data || { summary: null, payments: [], withdrawals: [] }));
      if (!silent) toast.success('Conciliação atualizada.');
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erro ao carregar conciliação');
      }
    } finally {
      setLoadingReconciliation(false);
    }
  };

  const fetchCashIn = async ({ silent = false } = {}) => {
    setLoadingCashIn(true);
    try {
      const response = await axios.get(`${API}/admin/cash-in/live?limit=200`, { withCredentials: true });
      setCashInData(normalizeObjectStrings(response.data || { summary: null, entries: [], updated_at: null }));
      setCashInLastRefresh(new Date());
      if (!silent) toast.success('Entradas pagas atualizadas.');
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erro ao carregar entradas pagas');
      }
    } finally {
      setLoadingCashIn(false);
    }
  };

  const fetchPartnerCodes = async ({ silent = false } = {}) => {
    setLoadingPartnerCodes(true);
    try {
      const response = await axios.get(`${API}/admin/partner-codes`, { withCredentials: true });
      setPartnerCodes(normalizeObjectStrings(response.data || []));
      if (!silent) {
        toast.success('Parcerias atualizadas.');
      }
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erro ao carregar parcerias');
      }
    } finally {
      setLoadingPartnerCodes(false);
    }
  };

  const fetchData = async () => {
    try {
      const [adminRes, statsRes, suppliersRes, withdrawalsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/me`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/suppliers`, { withCredentials: true }),
        axios.get(`${API}/admin/withdrawals`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setAdmin(normalizeObjectStrings(adminRes.data));
      setStats(normalizeObjectStrings(statsRes.data));
      setSuppliers(normalizeObjectStrings(suppliersRes.data || []));
      setWithdrawals(normalizeObjectStrings(withdrawalsRes.data || []));
      setUsers(normalizeObjectStrings(usersRes.data || []));

      // Fetch admins list if super admin
      if (adminRes.data.is_super_admin) {
        const adminsRes = await axios.get(`${API}/admin/admins`, { withCredentials: true });
        setAdmins(normalizeObjectStrings(adminsRes.data || []));
      }

      await fetchReconciliation(
        { payment_id: '', external_reference: '', payout_id: '' },
        { silent: true }
      );
      await fetchCashIn({ silent: true });
      await fetchPartnerCodes({ silent: true });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate('/admin/login');
    toast.success('Até logo!');
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/admins`, newAdminData, { withCredentials: true });
      toast.success('Admin criado com sucesso!');
      setShowAddAdminModal(false);
      setNewAdminData({ name: '', email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Tem certeza que deseja remover este admin?')) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, { withCredentials: true });
      toast.success('Admin removido!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover admin');
    }
  };

  const handleImpersonateUser = async (userItem) => {
    if (!userItem?.user_id) return;
    setImpersonatingUserId(userItem.user_id);
    try {
      await axios.post(`${API}/admin/users/${userItem.user_id}/impersonate`, {}, { withCredentials: true });
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao entrar como este usuário');
      setImpersonatingUserId(null);
    }
  };

  const handleSyncWithdrawal = async (withdrawalId) => {
    setProcessingWithdrawal(withdrawalId);
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/sync`, {}, { withCredentials: true });
      toast.success('Status do saque sincronizado.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar saque');
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const handleRejectWithdrawal = async (withdrawalId) => {
    const reason = window.prompt('Motivo da rejeição (opcional):');
    if (reason === null) return; // User cancelled
    setProcessingWithdrawal(withdrawalId);
    try {
      await axios.put(`${API}/admin/withdrawals/${withdrawalId}/reject`, { reason }, { withCredentials: true });
      toast.success('Saque rejeitado. O saldo foi devolvido ao usuário.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar saque');
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const handleSupplierApproval = async (supplier, approvalStatus) => {
    const requiresReason = approvalStatus === 'rejected';
    const reason = requiresReason
      ? window.prompt('Informe o motivo da reprovação para registrar no admin:') || ''
      : '';

    if (requiresReason && !reason.trim()) {
      toast.error('Informe o motivo antes de reprovar o parceiro.');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/suppliers/${supplier.supplier_id}/approval`,
        {
          approval_status: approvalStatus,
          reason: reason.trim() || null,
        },
        { withCredentials: true }
      );

      toast.success(
        approvalStatus === 'approved'
          ? 'Parceiro aprovado e cupom gerado com sucesso.'
          : approvalStatus === 'rejected'
            ? 'Cadastro reprovado com sucesso.'
            : 'Status do parceiro atualizado.'
      );
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar aprovação do parceiro');
    }
  };

  const handleSupplierMarketplaceUpdate = async (supplier, payload, successMessage) => {
    setProcessingSupplierId(supplier.supplier_id);
    try {
      await axios.put(
        `${API}/admin/suppliers/${supplier.supplier_id}/marketplace`,
        payload,
        { withCredentials: true }
      );
      toast.success(successMessage);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar configuracao do fornecedor');
    } finally {
      setProcessingSupplierId(null);
    }
  };

  const isPendingWithdrawal = (status) => ['pending', 'processing'].includes(status);
  const canRejectWithdrawal = (withdrawal) => withdrawal.status === 'pending' && !withdrawal.payout_id;
  const getWithdrawalStatusLabel = (status) => {
    if (status === 'completed') return 'Sacado';
    if (status === 'rejected') return 'Cancelado';
    if (status === 'processing') return 'Processando';
    if (status === 'pending') return 'Pendente';
    return 'Falhou';
  };
  const pendingWithdrawalsCount = withdrawals.filter((w) => isPendingWithdrawal(w.status)).length;
  const reconciliationSummary = reconciliationData?.summary || {};
  const reconciliationPayments = reconciliationData?.payments || [];
  const reconciliationWithdrawals = reconciliationData?.withdrawals || [];
  const cashInSummary = cashInData?.summary || {};
  const cashInEntries = cashInData?.entries || [];

  const handleReconciliationSearch = async (event) => {
    event.preventDefault();
    await fetchReconciliation(reconciliationFilters);
  };

  const handleReconciliationReset = async () => {
    const resetFilters = { payment_id: '', external_reference: '', payout_id: '' };
    setReconciliationFilters(resetFilters);
    await fetchReconciliation(resetFilters);
  };

  const openCreatePartnerCodeModal = () => {
    setEditingPartnerCode(null);
    setPartnerCodeForm(PARTNER_CODE_FORM_DEFAULT);
    setShowPartnerCodeModal(true);
  };

  const openEditPartnerCodeModal = (code) => {
    setEditingPartnerCode(code);
    setPartnerCodeForm({
      code: code?.code || '',
      label: code?.label || '',
      description: code?.description || '',
      fee_percentage: String(Number(code?.fee_percentage ?? 0)),
      max_redemptions: String(Number(code?.max_redemptions ?? 0)),
      expires_at: toDateTimeLocalValue(code?.expires_at),
      is_active: Boolean(code?.is_active),
    });
    setShowPartnerCodeModal(true);
  };

  const handleSavePartnerCode = async (event) => {
    event.preventDefault();

    const normalizedCode = String(partnerCodeForm.code || '').trim().toUpperCase();
    const label = String(partnerCodeForm.label || '').trim();

    if (!editingPartnerCode && normalizedCode.length < 4) {
      toast.error('Use um código com pelo menos 4 caracteres.');
      return;
    }

    if (label.length < 3) {
      toast.error('Informe um nome interno para identificar a parceria.');
      return;
    }

    const payload = {
      label,
      description: String(partnerCodeForm.description || '').trim() || null,
      fee_percentage: Number(partnerCodeForm.fee_percentage || 0),
      max_redemptions: Math.max(0, Number(partnerCodeForm.max_redemptions || 0)),
      expires_at: toApiDateTimeValue(partnerCodeForm.expires_at),
      is_active: Boolean(partnerCodeForm.is_active),
    };

    try {
      if (editingPartnerCode) {
        await axios.put(
          `${API}/admin/partner-codes/${editingPartnerCode.promo_code_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Parceria atualizada com sucesso.');
      } else {
        await axios.post(
          `${API}/admin/partner-codes`,
          { ...payload, code: normalizedCode },
          { withCredentials: true }
        );
        toast.success('Parceria criada com sucesso.');
      }

      setShowPartnerCodeModal(false);
      setEditingPartnerCode(null);
      setPartnerCodeForm(PARTNER_CODE_FORM_DEFAULT);
      await fetchPartnerCodes({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar parceria');
    }
  };

  const activePartnerCodesCount = partnerCodes.filter((code) => code.is_active && !code.expired).length;
  const redeemedPartnerCodesCount = partnerCodes.reduce((total, code) => total + Number(code.redemptions_count || 0), 0);
  const zeroFeePartnerCodesCount = partnerCodes.filter((code) => Number(code.fee_percentage || 0) === 0).length;
  const unlimitedPartnerCodesCount = partnerCodes.filter((code) => Number(code.max_redemptions || 0) === 0).length;

  const getPartnerApprovalBadge = (approvalStatus) => {
    switch ((approvalStatus || '').toLowerCase()) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'rejected':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  };

  const getPartnerApprovalLabel = (approvalStatus) => {
    switch ((approvalStatus || '').toLowerCase()) {
      case 'approved':
        return 'Aprovado';
      case 'rejected':
        return 'Reprovado';
      default:
        return 'Em análise';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EF]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#333333] font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8B7B1]/35">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#C8A97E]" />
              <span className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>Admin</span>
            </div>
            {admin?.is_super_admin && (
              <span className="bg-[#C8A97E]/20 text-[#C8A97E] text-xs px-2 py-1 rounded-full font-bold">
                SUPER ADMIN
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#333333] hidden sm:inline">{admin?.name}</span>
            <Button variant="outline" onClick={handleLogout} className="rounded-full border-[#E8B7B1]/35 text-[#333333] hover:bg-[#F6F1EF]" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {['overview', 'cashin', 'partnercodes', 'suppliers', 'withdrawals', 'reconciliation', ...(admin?.is_super_admin ? ['admins'] : [])].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className={`rounded-full whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-[#C8A97E] text-white hover:bg-[#B69669]' 
                  : 'text-[#333333] hover:text-[#1C1C1C] hover:bg-white'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'overview' && 'Visão Geral'}
              {tab === 'cashin' && 'Entradas em tempo real'}
              {tab === 'partnercodes' && 'Parcerias & cupons'}
              {tab === 'suppliers' && 'Parceiros'}
              {tab === 'withdrawals' && (
                <>
                  Saques
                  {pendingWithdrawalsCount > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {pendingWithdrawalsCount}
                    </span>
                  )}
                </>
              )}
              {tab === 'reconciliation' && 'Conciliação'}
              {tab === 'admins' && 'Administradores'}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
                <p className="text-sm text-[#333333] uppercase tracking-wider">Total Usuários</p>
                <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="stat-users">{stats.users.total}</p>
                <p className="text-xs text-[#333333] mt-1">
                  {stats.users.premium} premium • {stats.users.free} grátis
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Store className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
                <p className="text-sm text-[#333333] uppercase tracking-wider">Parceiros</p>
                <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="stat-suppliers">{stats.suppliers.total}</p>
                <p className="text-xs text-[#333333] mt-1">
                  {stats.suppliers.by_approval?.approved || 0} aprovados • {stats.suppliers.by_approval?.pending_review || 0} em análise
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-green-400" />
                  </div>
                </div>
                <p className="text-sm text-[#333333] uppercase tracking-wider">Sites Criados</p>
                <p className="text-2xl font-bold text-[#1C1C1C]" data-testid="stat-sites">{stats.sites}</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#C8A97E]/20 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-[#C8A97E]" />
                  </div>
                </div>
                <p className="text-sm text-[#333333] uppercase tracking-wider">Parcerias ativas</p>
                <p className="text-2xl font-bold text-[#1C1C1C]">{stats.partner_program?.active_codes || 0}</p>
                <p className="text-xs text-[#333333] mt-1">
                  {stats.partner_program?.total_redemptions || 0} ativações de cupom
                </p>
              </div>
            </div>

            {/* Partner Approval Overview */}
            <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
              <h3 className="text-lg font-bold text-[#1C1C1C] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#C8A97E]" />
                Aprovação de Parceiros
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-[#F6F1EF] rounded-lg p-4 border border-[#E8B7B1]/35">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-[#1C1C1C]">Em análise</span>
                    <span className="text-[#333333]">{stats.suppliers.by_approval?.pending_review || 0} parceiros</span>
                  </div>
                  <p className="text-sm text-[#333333]">Cadastros aguardando revisão do admin e liberação do cupom oficial.</p>
                </div>
                <div className="bg-[#F6F1EF] rounded-lg p-4 border border-[#E8B7B1]/35">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-[#1C1C1C]">Aprovados</span>
                    <span className="text-[#333333]">{stats.suppliers.by_approval?.approved || 0} parceiros</span>
                  </div>
                  <p className="text-sm text-[#333333]">Parceiros com cupom ativo para oferecer a condição especial da conta.</p>
                </div>
                <div className="bg-gradient-to-br from-[#C8A97E]/10 to-[#E8B7B1]/20 rounded-lg p-4 border border-[#C8A97E]/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-[#C8A97E]">Reprovados</span>
                    <span className="text-[#333333]">{stats.suppliers.by_approval?.rejected || 0} parceiros</span>
                  </div>
                  <p className="text-sm text-[#333333]">Cadastros recusados pela equipe, com motivo registrado no painel.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-[#E8B7B1]/35">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#1C1C1C] flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Últimos usuários cadastrados
                  </h3>
                  <p className="text-sm text-[#333333] mt-1">
                    Visualize quem criou conta no site, com nome, e-mail, plano e site vinculado.
                  </p>
                </div>
                <span className="text-xs text-[#333333]">
                  {users.length} registro{users.length === 1 ? '' : 's'} carregado{users.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-[#E8B7B1]/20">
                <table className="w-full">
                  <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/20">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Nome</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">E-mail</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Plano</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Site</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Criado em</th>
                        <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8B7B1]/20">
                      {users.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-center text-sm text-[#333333]">
                            Nenhum usuário encontrado.
                          </td>
                        </tr>
                      )}
                    {users.map((userItem) => (
                      <tr key={userItem.user_id} className="hover:bg-[#F6F1EF]/50">
                        <td className="px-4 py-3 text-sm text-[#1C1C1C]">
                          <p className="font-semibold">{userItem.name || 'Sem nome'}</p>
                          <p className="text-xs text-[#333333]">{userItem.user_id}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#333333]">{userItem.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-[#333333]">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex w-fit rounded-full px-2 py-1 text-xs font-semibold ${
                              userItem.subscription_plan === 'premium'
                                ? 'bg-[#C8A97E]/15 text-[#9A6B65]'
                                : 'bg-slate-100 text-[#333333]'
                            }`}>
                              {getUserPlanLabel(userItem)}
                            </span>
                            <span className="text-xs text-[#333333]">
                              Taxa efetiva: {Number(userItem.effective_fee_percentage || 3.59).toFixed(2)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#333333]">
                          {userItem.site_slug ? (
                            <div>
                              <p className="font-medium text-[#1C1C1C]">{userItem.site_couple_names || userItem.site_slug}</p>
                              <p className="text-xs">
                                /s/{userItem.site_slug} {userItem.site_is_published ? '• publicado' : '• rascunho'}
                              </p>
                            </div>
                          ) : (
                            <span className="text-xs text-[#333333]">Ainda sem site criado</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#333333]">{formatAdminDateTime(userItem.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonateUser(userItem)}
                            disabled={impersonatingUserId === userItem.user_id}
                            className="rounded-full border-[#C8A97E]/45 text-[#9A6B65] hover:bg-[#F6F1EF]"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            {impersonatingUserId === userItem.user_id ? 'Entrando...' : 'Entrar como usuário'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Cash-in Live Tab */}
        {activeTab === 'cashin' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Entradas pagas em tempo real
                </h2>
                <p className="text-sm text-[#333333] mt-1">
                  Todas as entradas aprovadas com forma de pagamento, pagador e recebedor na carteira.
                </p>
                {cashInLastRefresh && (
                  <p className="text-xs text-[#333333] mt-1">
                    Última atualização: {cashInLastRefresh.toLocaleTimeString('pt-BR')}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => fetchCashIn()}
                disabled={loadingCashIn}
                className="rounded-full border-[#E8B7B1]/35 text-[#333333]"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingCashIn ? 'animate-spin' : ''}`} />
                Atualizar agora
              </Button>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Entradas aprovadas</p>
                <p className="text-xl font-bold text-[#1C1C1C]">{cashInSummary.count || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Total bruto (R$)</p>
                <p className="text-xl font-bold text-[#1C1C1C]">{(cashInSummary.gross_total || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Taxas (R$)</p>
                <p className="text-xl font-bold text-[#B97D5B]">{(cashInSummary.fee_total || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Líquido carteira (R$)</p>
                <p className="text-xl font-bold text-green-500">{(cashInSummary.net_total || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
              <div className="px-4 py-3 border-b border-[#E8B7B1]/20 flex items-center justify-between">
                <h3 className="font-semibold text-[#1C1C1C] flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-[#9A6B65]" />
                  Entradas na carteira
                </h3>
                <span className="text-xs text-[#333333]">{cashInEntries.length} registros</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Data / ID</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Pagamento</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Quem pagou</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Quem recebe</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Casal / Presente</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Bruto</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Taxa</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Líquido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8B7B1]/20">
                    {cashInEntries.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-sm text-[#333333]">
                          Nenhuma entrada aprovada no período consultado.
                        </td>
                      </tr>
                    )}
                    {cashInEntries.map((entry) => (
                      <tr key={`${entry.payment_id}-${entry.provider_payment_id || 'provider'}`} className="hover:bg-[#F6F1EF]/50">
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{entry.paid_at ? new Date(entry.paid_at).toLocaleString('pt-BR') : '-'}</p>
                          <p><strong>payment_id:</strong> {entry.payment_id || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <span className={`px-2 py-1 rounded-full ${
                            entry.payment_method === 'credit_card'
                              ? 'bg-blue-500/15 text-blue-500'
                              : entry.payment_method === 'pix'
                                ? 'bg-green-500/15 text-green-500'
                                : 'bg-slate-200 text-[#333333]'
                          }`}>
                            {entry.payment_method_label || '-'}
                          </span>
                          <p className="mt-1">{entry.provider || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{entry.payer_name || '-'}</p>
                          <p>{entry.payer_email || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{entry.receiver_name || '-'}</p>
                          <p>{entry.receiver_role === 'noivo' ? 'Noivo' : 'Noiva'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{entry.couple_names || '-'}</p>
                          <p>{entry.gift_name || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-[#1C1C1C]">R$ {(entry.gross_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-[#B97D5B]">R$ {(entry.fee_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-500">R$ {(entry.net_amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Suppliers Tab */}
        {activeTab === 'suppliers' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                Parceiros
              </h2>
              <span className="text-[#333333]">{suppliers.length} total</span>
            </div>

            {suppliers.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-[#E8B7B1]/35">
                <Store className="w-16 h-16 text-[#333333] mx-auto mb-4" />
                <p className="text-[#333333]">Nenhum parceiro cadastrado</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Parceiro</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Contato</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Categoria / local</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Documento</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Status</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Marketplace beta</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Cupom</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Usos do cupom</th>
                        <th className="text-right px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8B7B1]/20">
                      {suppliers.map((sup) => (
                        <tr key={sup.supplier_id} className="hover:bg-[#F6F1EF]/50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-[#1C1C1C]">{sup.name || sup.company_name || sup.contact_name || '-'}</p>
                              <p className="text-xs text-[#333333]">{sup.company_name || 'Cadastro individual'}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-[#333333]">{sup.contact_name || sup.name || '-'}</p>
                              <p className="text-xs text-[#333333]">{sup.email}</p>
                              <p className="text-xs text-[#333333]">{sup.phone || '-'}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#333333]">
                            <p>{sup.category || '-'}</p>
                            <p className="text-xs text-[#9A6B65]">{sup.city || '-'}{sup.state ? `, ${sup.state}` : ''}</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#333333]">
                            {(sup.document_type || 'CPF/CNPJ').toUpperCase()} {sup.document || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${getPartnerApprovalBadge(sup.approval_status)}`}>
                                {getPartnerApprovalLabel(sup.approval_status)}
                              </span>
                              {sup.registration_2fa_pending && (
                                <p className="text-[11px] text-[#B97D5B]">Aguardando confirmação por código</p>
                              )}
                              {sup.approval_reason && (
                                <p className="max-w-[220px] text-[11px] text-red-500">{sup.approval_reason}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#333333]">
                            <div className="space-y-2">
                              <p className="font-semibold text-[#1C1C1C]">{sup.plan_type === 'premium_plus' ? 'Premium Plus' : sup.plan_type === 'premium' ? 'Premium' : 'Free'}</p>
                              <p className="text-xs">
                                Diretorio: {sup.is_marketplace_published ? 'Publicado' : 'Oculto'}
                              </p>
                              <p className="text-xs">
                                Home: {sup.is_homepage_featured ? 'Destaque' : 'Rotacao normal'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#333333]">
                            {sup.partner_coupon_code ? (
                              <div>
                                <p className="font-semibold text-[#1C1C1C]">{sup.partner_coupon_code}</p>
                                <p className="text-xs text-[#333333]">
                                  {(Number(sup.partner_coupon_fee_percentage ?? 3)).toFixed(2).replace('.', ',')}%
                                </p>
                              </div>
                            ) : (
                              <span className="text-xs text-[#B97D5B]">Cupom ainda não liberado</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#333333]">{sup.partner_coupon_redemptions_count || 0}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                              {sup.approval_status !== 'approved' && !sup.registration_2fa_pending && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSupplierApproval(sup, 'approved')}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                                  data-testid={`approve-supplier-${sup.supplier_id}`}
                                >
                                  Aprovar
                                </Button>
                              )}
                              {sup.approval_status !== 'rejected' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSupplierApproval(sup, 'rejected')}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                  data-testid={`reject-supplier-${sup.supplier_id}`}
                                >
                                  Reprovar
                                </Button>
                              )}
                              {sup.approval_status === 'approved' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={processingSupplierId === sup.supplier_id}
                                    onClick={() => handleSupplierMarketplaceUpdate(
                                      sup,
                                      { is_marketplace_published: !sup.is_marketplace_published },
                                      sup.is_marketplace_published ? 'Fornecedor ocultado do diretorio.' : 'Fornecedor publicado no diretorio.'
                                    )}
                                    className="text-[#9A6B65] hover:text-[#7A5D52] hover:bg-[#E8B7B1]/10"
                                  >
                                    {sup.is_marketplace_published ? 'Ocultar' : 'Publicar'}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={processingSupplierId === sup.supplier_id || !sup.is_marketplace_published}
                                    onClick={() => handleSupplierMarketplaceUpdate(
                                      sup,
                                      { is_homepage_featured: !sup.is_homepage_featured },
                                      sup.is_homepage_featured ? 'Fornecedor removido do destaque da home.' : 'Fornecedor destacado na home.'
                                    )}
                                    className="text-[#9A6B65] hover:text-[#7A5D52] hover:bg-[#E8B7B1]/10"
                                  >
                                    {sup.is_homepage_featured ? 'Remover destaque' : 'Destacar'}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={processingSupplierId === sup.supplier_id}
                                    onClick={() => handleSupplierMarketplaceUpdate(
                                      sup,
                                      {
                                        plan_type:
                                          sup.plan_type === 'free'
                                            ? 'premium'
                                            : sup.plan_type === 'premium'
                                              ? 'premium_plus'
                                              : 'free',
                                      },
                                      'Plano do fornecedor atualizado.'
                                    )}
                                    className="text-[#9A6B65] hover:text-[#7A5D52] hover:bg-[#E8B7B1]/10"
                                  >
                                    Alternar plano
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                Gerenciamento de Saques
              </h2>
              <div className="flex gap-3">
                <span className="bg-yellow-500/20 text-yellow-400 text-sm px-3 py-1 rounded-full">
                  {pendingWithdrawalsCount} pendentes
                </span>
                <span className="bg-green-500/20 text-green-400 text-sm px-3 py-1 rounded-full">
                  {withdrawals.filter(w => w.status === 'completed').length} sacados
                </span>
              </div>
            </div>

            {/* Alert Banner */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 font-medium">Fluxo Automático</p>
                <p className="text-amber-200/70 text-sm">
                  O sistema envia o PIX automaticamente. O status só vira "Sacado" quando o provedor confirmar.
                  Use "Atualizar" para sincronizar o status em tempo real.
                </p>
              </div>
            </div>

            {withdrawals.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center border border-[#E8B7B1]/35">
                <Wallet className="w-16 h-16 text-[#333333] mx-auto mb-4" />
                <p className="text-[#333333]">Nenhuma solicitação de saque</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                      <tr>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Data</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Usuário</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Chave PIX</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Valor</th>
                        <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Status</th>
                        <th className="text-right px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8B7B1]/20">
                      {withdrawals.map((w) => (
                        <tr key={w.withdrawal_id} className="hover:bg-[#F6F1EF]/50">
                          <td className="px-6 py-4 text-sm text-[#333333]">
                            {new Date(w.created_at).toLocaleDateString('pt-BR')}
                            <p className="text-xs text-[#333333]">
                              {new Date(w.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm text-[#1C1C1C]">{w.user_info?.name || w.user_info?.company_name || 'Usuário'}</p>
                              <p className="text-xs text-[#333333]">{w.user_info?.email}</p>
                              {w.type === 'supplier' && (
                                <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded mt-1 inline-block">Fornecedor</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="bg-[#F6F1EF] text-[#333333] text-xs px-2 py-1 rounded uppercase">
                                {w.pix_key_type}
                              </span>
                              <code className="text-sm text-[#333333] font-mono">{w.pix_key}</code>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-[#C8A97E]">
                            R$ {w.amount?.toFixed(2)}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${
                              w.status === 'completed' 
                                ? 'bg-green-500/20 text-green-400'
                                : w.status === 'rejected'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {w.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                              {w.status === 'rejected' && <XCircle className="w-3 h-3" />}
                              {isPendingWithdrawal(w.status) && <Clock className="w-3 h-3" />}
                              {getWithdrawalStatusLabel(w.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {isPendingWithdrawal(w.status) ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleSyncWithdrawal(w.withdrawal_id)}
                                  disabled={processingWithdrawal === w.withdrawal_id}
                                  className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs"
                                  data-testid={`sync-withdrawal-${w.withdrawal_id}`}
                                >
                                  <Clock className="w-3 h-3 mr-1" />
                                  Atualizar
                                </Button>
                                {canRejectWithdrawal(w) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectWithdrawal(w.withdrawal_id)}
                                    disabled={processingWithdrawal === w.withdrawal_id}
                                    className="border-red-500 text-red-400 hover:bg-red-500/10 rounded-full text-xs"
                                    data-testid={`reject-withdrawal-${w.withdrawal_id}`}
                                  >
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Cancelar
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-[#333333]">
                                {w.processed_at ? new Date(w.processed_at).toLocaleDateString('pt-BR') : '-'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reconciliation Tab */}
        {activeTab === 'reconciliation' && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Conciliação de Pagamentos e Saques
                </h2>
                <p className="text-sm text-[#333333] mt-1">
                  Consulte pagamentos e transferências com filtros por payment_id, externalReference e payout_id.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => fetchReconciliation(reconciliationFilters)}
                disabled={loadingReconciliation}
                className="rounded-full border-[#E8B7B1]/35 text-[#333333]"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingReconciliation ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>

            <form
              onSubmit={handleReconciliationSearch}
              className="bg-white rounded-xl p-4 border border-[#E8B7B1]/35 grid gap-3 md:grid-cols-4"
            >
              <div>
                <Label className="text-xs text-[#333333] uppercase tracking-wider mb-1 block">payment_id</Label>
                <Input
                  value={reconciliationFilters.payment_id}
                  onChange={(event) => setReconciliationFilters((current) => ({ ...current, payment_id: event.target.value }))}
                  placeholder="pay_ ou card_..."
                />
              </div>
              <div>
                <Label className="text-xs text-[#333333] uppercase tracking-wider mb-1 block">externalReference</Label>
                <Input
                  value={reconciliationFilters.external_reference}
                  onChange={(event) => setReconciliationFilters((current) => ({ ...current, external_reference: event.target.value }))}
                  placeholder="externalReference..."
                />
              </div>
              <div>
                <Label className="text-xs text-[#333333] uppercase tracking-wider mb-1 block">payout_id</Label>
                <Input
                  value={reconciliationFilters.payout_id}
                  onChange={(event) => setReconciliationFilters((current) => ({ ...current, payout_id: event.target.value }))}
                  placeholder="UUID da transferência..."
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" className="flex-1 bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full">
                  <Search className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
                <Button type="button" variant="outline" onClick={handleReconciliationReset} className="rounded-full">
                  Limpar
                </Button>
              </div>
            </form>

            <div className="grid md:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Pagamentos</p>
                <p className="text-xl font-bold text-[#1C1C1C]">{reconciliationSummary.payments_count || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Bruto (R$)</p>
                <p className="text-xl font-bold text-[#1C1C1C]">{(reconciliationSummary.payment_gross_total || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Taxa (R$)</p>
                <p className="text-xl font-bold text-[#B97D5B]">{(reconciliationSummary.payment_fee_total || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-xl border border-[#E8B7B1]/35 p-4">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Saques (R$)</p>
                <p className="text-xl font-bold text-[#1C1C1C]">{(reconciliationSummary.withdrawal_total || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
              <div className="px-4 py-3 border-b border-[#E8B7B1]/20 flex items-center justify-between">
                <h3 className="font-semibold text-[#1C1C1C] flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#9A6B65]" />
                  Pagamentos
                </h3>
                <span className="text-xs text-[#333333]">{reconciliationPayments.length} registros</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">IDs</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Pagador</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Presente / Casal</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Bruto</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Taxa</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Líquido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8B7B1]/20">
                    {reconciliationPayments.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-sm text-[#333333]">
                          Nenhum pagamento encontrado para os filtros informados.
                        </td>
                      </tr>
                    )}
                    {reconciliationPayments.map((payment) => (
                      <tr key={`${payment.payment_id}-${payment.provider_payment_id}`} className="hover:bg-[#F6F1EF]/50">
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p><strong>payment_id:</strong> {payment.payment_id || '-'}</p>
                          <p><strong>externalReference:</strong> {payment.external_reference || '-'}</p>
                          <p><strong>provider_id:</strong> {payment.provider_payment_id || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`px-2 py-1 rounded-full ${
                            payment.status === 'approved'
                              ? 'bg-green-500/20 text-green-500'
                              : payment.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-500'
                                : 'bg-red-500/20 text-red-500'
                          }`}>
                            {payment.status || 'unknown'}
                          </span>
                          <p className="text-[#333333] mt-1">{payment.provider_status || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{payment.payer_name || '-'}</p>
                          <p>{payment.payer_email || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{payment.gift_name || payment.gift_id || '-'}</p>
                          <p>{payment.couple_names || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-[#1C1C1C]">R$ {(payment.gross_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-[#B97D5B]">R$ {(payment.fee_amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-[#1C1C1C]">R$ {(payment.net_amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
              <div className="px-4 py-3 border-b border-[#E8B7B1]/20 flex items-center justify-between">
                <h3 className="font-semibold text-[#1C1C1C] flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#9A6B65]" />
                  Saques
                </h3>
                <span className="text-xs text-[#333333]">{reconciliationWithdrawals.length} registros</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">IDs</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Usuário</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">PIX</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-[#333333] uppercase tracking-wider">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8B7B1]/20">
                    {reconciliationWithdrawals.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-[#333333]">
                          Nenhum saque encontrado para os filtros informados.
                        </td>
                      </tr>
                    )}
                    {reconciliationWithdrawals.map((withdrawal) => (
                      <tr key={`${withdrawal.withdrawal_id}-${withdrawal.payout_id || 'no-payout'}`} className="hover:bg-[#F6F1EF]/50">
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p><strong>withdrawal_id:</strong> {withdrawal.withdrawal_id || '-'}</p>
                          <p><strong>payout_id:</strong> {withdrawal.payout_id || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <span className={`px-2 py-1 rounded-full ${
                            withdrawal.status === 'completed'
                              ? 'bg-green-500/20 text-green-500'
                              : ['pending', 'processing'].includes(withdrawal.status)
                                ? 'bg-yellow-500/20 text-yellow-500'
                                : 'bg-red-500/20 text-red-500'
                          }`}>
                            {withdrawal.status || 'unknown'}
                          </span>
                          <p className="mt-1">{withdrawal.provider_status || '-'}</p>
                          {(withdrawal.failure_reason || withdrawal.provider_last_error) && (
                            <p className="mt-1 text-red-500">
                              {withdrawal.failure_reason || withdrawal.provider_last_error}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{withdrawal.user_name || '-'}</p>
                          <p>{withdrawal.user_email || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#333333]">
                          <p>{withdrawal.pix_key_type || '-'}</p>
                          <p>{withdrawal.pix_key || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-[#1C1C1C]">
                          R$ {(withdrawal.amount || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Partner Codes Tab */}
        {activeTab === 'partnercodes' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Parcerias e cupons de taxa
                </h2>
                <p className="mt-1 text-sm text-[#333333]">
                  Apenas admins criam os codigos. Quando o casal aplica, a taxa efetiva fica gravada na conta para os proximos presentes.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => fetchPartnerCodes()}
                  disabled={loadingPartnerCodes}
                  className="rounded-full border-[#E8B7B1]/35 text-[#333333]"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingPartnerCodes ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                <Button
                  onClick={openCreatePartnerCodeModal}
                  className="rounded-full bg-[#C8A97E] text-white hover:bg-[#B69669]"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Nova parceria
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Códigos ativos</p>
                <p className="mt-2 text-2xl font-bold text-[#1C1C1C]">{activePartnerCodesCount}</p>
              </div>
              <div className="rounded-xl border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Resgates realizados</p>
                <p className="mt-2 text-2xl font-bold text-[#1C1C1C]">{redeemedPartnerCodesCount}</p>
              </div>
              <div className="rounded-xl border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Taxa zero</p>
                <p className="mt-2 text-2xl font-bold text-[#1C1C1C]">{zeroFeePartnerCodesCount}</p>
              </div>
              <div className="rounded-xl border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs uppercase tracking-wider text-[#333333]">Sem limite de uso</p>
                <p className="mt-2 text-2xl font-bold text-[#1C1C1C]">{unlimitedPartnerCodesCount}</p>
              </div>
            </div>

            {partnerCodes.length === 0 ? (
              <div className="rounded-xl border border-[#E8B7B1]/35 bg-white p-12 text-center">
                <Link2 className="mx-auto mb-4 h-14 w-14 text-[#C8A97E]" />
                <h3 className="text-lg font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Nenhuma parceria cadastrada
                </h3>
                <p className="mt-2 text-sm text-[#333333]">
                  Crie um código para parceiros, campanhas ou acessos com taxa especial.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {partnerCodes.map((code) => (
                  <article key={code.promo_code_id} className="rounded-2xl border border-[#E8B7B1]/35 bg-white p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#F6F1EF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#9A6B65]">
                            {code.code}
                          </span>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            code.expired
                              ? 'bg-red-100 text-red-600'
                              : code.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-[#333333]'
                          }`}>
                            {code.expired ? 'Expirado' : code.is_active ? 'Ativo' : 'Pausado'}
                          </span>
                        </div>
                        <h3 className="mt-3 text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                          {code.label}
                        </h3>
                        <p className="mt-1 text-sm text-[#333333]">
                          {code.description || 'Sem descricao interna.'}
                        </p>
                      </div>

                      <Button
                        variant="outline"
                        onClick={() => openEditPartnerCodeModal(code)}
                        className="rounded-full border-[#E8B7B1]/35 text-[#333333]"
                      >
                        Editar
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                      <div className="rounded-xl bg-[#F6F1EF] p-3">
                        <p className="text-[11px] uppercase tracking-wider text-[#333333]">Taxa aplicada</p>
                        <p className="mt-1 text-lg font-bold text-[#1C1C1C]">{Number(code.fee_percentage || 0).toFixed(2).replace('.', ',')}%</p>
                      </div>
                      <div className="rounded-xl bg-[#F6F1EF] p-3">
                        <p className="text-[11px] uppercase tracking-wider text-[#333333]">Usos</p>
                        <p className="mt-1 text-lg font-bold text-[#1C1C1C]">{code.redemptions_count || 0}</p>
                      </div>
                      <div className="rounded-xl bg-[#F6F1EF] p-3">
                        <p className="text-[11px] uppercase tracking-wider text-[#333333]">Limite</p>
                        <p className="mt-1 text-lg font-bold text-[#1C1C1C]">
                          {Number(code.max_redemptions || 0) === 0 ? 'Livre' : code.max_redemptions}
                        </p>
                      </div>
                      <div className="rounded-xl bg-[#F6F1EF] p-3">
                        <p className="text-[11px] uppercase tracking-wider text-[#333333]">Expiração</p>
                        <p className="mt-1 text-sm font-semibold text-[#1C1C1C]">
                          {code.expires_at ? new Date(code.expires_at).toLocaleDateString('pt-BR') : 'Sem prazo'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 rounded-xl border border-[#E8B7B1]/25 bg-[#FDF9F7] p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A6B65]">Resgates recentes</p>
                          <p className="text-xs text-[#333333]">
                            {code.created_by_admin_name ? `Criado por ${code.created_by_admin_name}` : 'Criado no admin'}
                          </p>
                        </div>
                        {code.remaining_redemptions !== null && (
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#333333]">
                            Restam {code.remaining_redemptions}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-2">
                        {(code.recent_redemptions || []).length === 0 && (
                          <p className="text-sm text-[#333333]">Nenhum casal usou este código ainda.</p>
                        )}
                        {(code.recent_redemptions || []).map((redemption) => (
                          <div key={`${code.promo_code_id}-${redemption.user_id}-${redemption.redeemed_at}`} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-[#1C1C1C]">{redemption.user_name || redemption.user_email || 'Usuario'}</p>
                              <p className="truncate text-xs text-[#333333]">{redemption.user_email || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-semibold text-[#C8A97E]">{Number(redemption.applied_fee_percentage || 0).toFixed(2).replace('.', ',')}%</p>
                              <p className="text-[11px] text-[#333333]">
                                {redemption.redeemed_at ? new Date(redemption.redeemed_at).toLocaleDateString('pt-BR') : '-'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admins Tab (Super Admin Only) */}
        {activeTab === 'admins' && admin?.is_super_admin && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                Administradores
              </h2>
              <Button
                onClick={() => setShowAddAdminModal(true)}
                className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full"
                data-testid="add-admin-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Adicionar Admin
              </Button>
            </div>

            <div className="bg-white rounded-xl overflow-hidden border border-[#E8B7B1]/35">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F6F1EF] border-b border-[#E8B7B1]/35">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Nome</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">E-mail</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Tipo</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Criado em</th>
                      <th className="text-right px-6 py-4 text-xs font-bold text-[#333333] uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8B7B1]/20">
                    {admins.map((adm) => (
                      <tr key={adm.admin_id} className="hover:bg-[#F6F1EF]/50">
                        <td className="px-6 py-4 text-sm font-medium text-[#1C1C1C]">{adm.name}</td>
                        <td className="px-6 py-4 text-sm text-[#333333]">{adm.email}</td>
                        <td className="px-6 py-4">
                          {adm.is_super_admin ? (
                            <span className="px-2 py-1 rounded text-xs font-bold bg-[#C8A97E]/20 text-[#C8A97E]">
                              Super Admin
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-[#333333]">
                              Admin
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#333333]">
                          {new Date(adm.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {!adm.is_super_admin && adm.admin_id !== admin.admin_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAdmin(adm.admin_id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-admin-${adm.admin_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Admin Modal */}
      <Dialog open={showAddAdminModal} onOpenChange={setShowAddAdminModal}>
        <DialogContent className="max-w-md bg-white border-[#E8B7B1]/35">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              Adicionar Administrador
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <DialogDescription className="text-[#333333] text-sm">
              Adicione um novo administrador ao sistema. Ele terá acesso ao painel mas não será super admin.
            </DialogDescription>
            <div>
              <Label className="text-sm font-bold text-[#333333] uppercase tracking-wider mb-2 block">
                Nome
              </Label>
              <Input
                value={newAdminData.name}
                onChange={(e) => setNewAdminData({ ...newAdminData, name: e.target.value })}
                placeholder="Nome do admin"
                className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                required
                data-testid="new-admin-name"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#333333] uppercase tracking-wider mb-2 block">
                E-mail
              </Label>
              <Input
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                placeholder="admin@example.com"
                className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                required
                data-testid="new-admin-email"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#333333] uppercase tracking-wider mb-2 block">
                Senha
              </Label>
              <Input
                type="password"
                value={newAdminData.password}
                onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                placeholder="Digite a senha"
                className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                required
                data-testid="new-admin-password"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddAdminModal(false)} className="rounded-full border-[#E8B7B1]/35 text-[#333333]">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#C8A97E] hover:bg-[#B69669] text-white rounded-full">
                Criar Admin
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showPartnerCodeModal} onOpenChange={setShowPartnerCodeModal}>
        <DialogContent className="max-w-2xl bg-white border-[#E8B7B1]/35">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
              {editingPartnerCode ? 'Editar parceria' : 'Nova parceria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePartnerCode} className="space-y-4">
            <DialogDescription className="text-sm text-[#333333]">
              Configure o código, a taxa especial e o limite de usos antes da expiração.
            </DialogDescription>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                  Código
                </Label>
                <Input
                  value={partnerCodeForm.code}
                  onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, code: event.target.value.toUpperCase() })}
                  placeholder="INFLU_JULIANA"
                  disabled={Boolean(editingPartnerCode)}
                  className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                  required
                />
                <p className="mt-1 text-xs text-[#333333]">
                  Use letras, números, hífen ou underline.
                </p>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                  Nome interno
                </Label>
                <Input
                  value={partnerCodeForm.label}
                  onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, label: event.target.value })}
                  placeholder="Influenciadora Juliana"
                  className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                Descrição
              </Label>
              <textarea
                value={partnerCodeForm.description}
                onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, description: event.target.value })}
                placeholder="Parceria com 0% de taxa para os primeiros 20 casais."
                className="min-h-[104px] w-full rounded-lg border border-[#E8B7B1]/35 bg-[#F6F1EF] px-4 py-3 text-[#1C1C1C] outline-none transition-colors focus:border-[#C8A97E] focus:ring-2 focus:ring-[#C8A97E]/20"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                  Taxa (%)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={partnerCodeForm.fee_percentage}
                  onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, fee_percentage: event.target.value })}
                  className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                  required
                />
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                  Max. de usos
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={partnerCodeForm.max_redemptions}
                  onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, max_redemptions: event.target.value })}
                  className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                  required
                />
                <p className="mt-1 text-xs text-[#333333]">Use 0 para deixar sem limite.</p>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                  Expira em
                </Label>
                <Input
                  type="datetime-local"
                  value={partnerCodeForm.expires_at}
                  onChange={(event) => setPartnerCodeForm({ ...partnerCodeForm, expires_at: event.target.value })}
                  className="h-12 rounded-lg bg-[#F6F1EF] border-[#E8B7B1]/35 text-[#1C1C1C]"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#333333]">
                Status
              </Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setPartnerCodeForm({ ...partnerCodeForm, is_active: true })}
                  className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                    partnerCodeForm.is_active
                      ? 'border-[#C8A97E] bg-[#C8A97E]/10 text-[#1C1C1C]'
                      : 'border-[#E8B7B1]/35 bg-[#F6F1EF] text-[#333333]'
                  }`}
                >
                  <p className="font-semibold">Ativo</p>
                  <p className="text-sm">Aceita novos resgates agora.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPartnerCodeForm({ ...partnerCodeForm, is_active: false })}
                  className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                    !partnerCodeForm.is_active
                      ? 'border-[#C8A97E] bg-[#C8A97E]/10 text-[#1C1C1C]'
                      : 'border-[#E8B7B1]/35 bg-[#F6F1EF] text-[#333333]'
                  }`}
                >
                  <p className="font-semibold">Pausado</p>
                  <p className="text-sm">Mantem o historico, mas bloqueia novos usos.</p>
                </button>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPartnerCodeModal(false)}
                className="rounded-full border-[#E8B7B1]/35 text-[#333333]"
              >
                Cancelar
              </Button>
              <Button type="submit" className="rounded-full bg-[#C8A97E] text-white hover:bg-[#B69669]">
                {editingPartnerCode ? 'Salvar alterações' : 'Criar parceria'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}











